import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, List, ListItem, styled } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelectIsWeatherAnimationOn } from '../../widgetManager';
import { useWindData } from '../model';

const PREFIX = 'TSMED-WindTimeBar';
const windTimeBarClasses = {
  playControl: `${PREFIX}-playControl`,
  playControlButton: `${PREFIX}-playControlButton`,
  playControlPlay: `${PREFIX}-playControlPlay`,
  playControlPause: `${PREFIX}-playControlPause`,
  playControlProgress: `${PREFIX}-playControlProgress`,
  timelineContainer: `${PREFIX}-timelineContainer`,
  timelineLabel: `${PREFIX}-timelineLabel`,
  timelineListContainer: `${PREFIX}-timelineListContainer`,
  timelineList: `${PREFIX}-timelineList`,
  timelineListItem: `${PREFIX}-timelineListItem`,
  timelineMarkerCenter: `${PREFIX}-timelineMarkerCenter`,
};

const Root = styled(Box, {
  shouldForwardProp: prop =>
    !['heatMapChangeTimeout', 'zeroTimeIndex'].includes(prop as string),
})<{ heatMapChangeTimeout: number; zeroTimeIndex: number }>(
  ({ heatMapChangeTimeout, zeroTimeIndex, theme: { spacing, palette } }) => ({
    position: 'absolute',
    top: spacing(1),
    left: spacing(1),
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 10,
    background:
      'linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), rgba(29, 42, 55, 0.50)',
    backdropFilter: 'blur(10px)',
    [`.${windTimeBarClasses.playControl}`]: {
      padding: spacing(1),
      alignContent: 'center',
      [`.${windTimeBarClasses.playControlButton}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        ['> svg']: {
          width: spacing(3),
          height: spacing(3),
          fill: palette.grey[200],
        },
      },
      [`.${windTimeBarClasses.playControlPlay}`]: {},
      [`.${windTimeBarClasses.playControlPause}`]: {
        color: palette.error.main,
      },
      [`.${windTimeBarClasses.playControlProgress}`]: {
        width: spacing(3),
        height: spacing(3),
      },
    },
    [`.${windTimeBarClasses.timelineContainer}`]: {
      boxSizing: 'content-box',
      display: 'flex',
      flexDirection: 'column',
      padding: '6px 8px',
      fontSize: '14px',
      gap: spacing(0.5),
      [`.${windTimeBarClasses.timelineMarkerCenter}`]: {
        position: 'absolute',
        top: 0,
        left: '50%',
        width: 2,
        height: '100%',
        transform: 'translateX(-1px)',
        background: 'rgba(255,255,255,0.70)',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.3,
      },
      [`.${windTimeBarClasses.timelineLabel}`]: {
        textAlign: 'center',
        fontFamily: 'JetBrains Mono',
        letterSpacing: -0.56,
        fontVariantNumeric: 'slashed-zero',
        lineHeight: 1,
        fontSize: '12px',
      },
      [`.${windTimeBarClasses.timelineListContainer}`]: {
        width: 120,
        height: spacing(2.25),
        overflow: 'hidden',
        position: 'relative',
        gap: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',

        [`.${windTimeBarClasses.timelineList}`]: {
          display: 'flex',
          flexDirection: 'row',
          position: 'absolute',
          willChange: 'transform',
          transition: `transform ${heatMapChangeTimeout}s ease-in-out`,
          zIndex: 1,
        },
        [`.${windTimeBarClasses.timelineListItem}`]: {
          width: spacing(3),
          height: spacing(1.5),
          padding: 0,
          flexShrink: 0,
          cursor: 'pointer',
          position: 'relative',
          transition: `opacity ${heatMapChangeTimeout}s ease-in-out, transform ${heatMapChangeTimeout}s ease-in-out`,
          willChange: 'transform, opacity',
          transformOrigin: '50% 50%',

          ['&:before']: {
            content: '""',
            position: 'absolute',
            left: '50%',
            marginLeft: -1,
            width: 2,
            height: '100%',
            background: 'rgba(255, 255, 255, 0.70)',
            top: 0,
          },
          [`&:nth-of-type(${zeroTimeIndex + 1}):before`]: {
            background: 'rgba(0, 204, 255, 0.70)',
          },
        },
      },
    },
  }),
);

// device-pixel snapping to reduce subpixel AA during motion
const pxSnap = (x: number) => {
  const dpr = window.devicePixelRatio || 1;
  return Math.round(x * dpr) / dpr;
};

export const WindTimeBar = ({
  heatMapChangeTimeout,
}: {
  heatMapChangeTimeout: number;
}) => {
  const { windParams, setWindParams, windParamsSet, cacheStats, setPreload } =
    useWindData();
  const weatherAnimationOn = useSelectIsWeatherAnimationOn();
  const loopIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Guards the one-shot autostart so it fires once per mount. See the autostart
  // effect below for why this must not be removed.
  const didAutostartRef = useRef(false);
  const [isPlayMode, setIsPlayMode] = useState(false);

  const listRef = useRef<HTMLUListElement | null>(null);

  const [activeParamsIndex, setActiveParamsIndex] = useState<number>(1);

  const zeroTimeIndex = useMemo(() => {
    const index = windParamsSet.findIndex(param =>
      param.dateTime?.isAfter(new Date()),
    );
    return index - 1;
  }, [windParamsSet]);

  const status = useMemo(() => {
    if (cacheStats.progress < 100) {
      return 'loading';
    }
    return 'ready';
  }, [cacheStats.progress]);

  const handlePlayMode = useCallback(
    (play: boolean) => {
      setPreload(play);
      setIsPlayMode(play);
      setActiveParamsIndex(zeroTimeIndex);
    },
    [setPreload, zeroTimeIndex],
  );

  // Auto-start the forecast time-lapse when the "Animate weather autostart"
  // setting is on (TSMED-949). Fires once per mount, so it re-arms every time
  // the wind map (re)appears, and once if the setting is flipped on while
  // mounted. The in-bar ▶/⏹ stays the live control: a user ⏹ is respected for
  // the rest of the mount because the ref is already set. The flag governs
  // auto-play only — toggling it off does not stop a running animation.
  //
  // The `didAutostartRef` guard is REQUIRED: `handlePlayMode`'s identity is
  // unstable (it depends on `zeroTimeIndex`, which changes on the hourly
  // `windParamsSet` refresh), so this effect re-runs periodically. Without the
  // guard, a re-run would re-fire `handlePlayMode(true)` and snap the cursor
  // back to "now" mid-playback (and override a user ⏹).
  useEffect(() => {
    if (didAutostartRef.current) {
      return;
    }
    if (!weatherAnimationOn || windParamsSet.length === 0) {
      return;
    }
    didAutostartRef.current = true;
    // Intentional one-shot setState-in-effect: play mode can only be entered
    // after mount, and the ref guard above prevents the cascading re-renders the
    // rule warns about.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handlePlayMode(true);
  }, [weatherAnimationOn, windParamsSet.length, handlePlayMode]);

  useEffect(() => {
    if (loopIntervalRef.current) {
      clearInterval(loopIntervalRef.current);
    }
    if (!isPlayMode || status !== 'ready') {
      return;
    }
    loopIntervalRef.current = setInterval(() => {
      setActiveParamsIndex(prev => (prev + 1) % windParamsSet.length);
    }, heatMapChangeTimeout * 1000);

    return () => {
      if (loopIntervalRef.current) {
        clearInterval(loopIntervalRef.current);
      }
    };
  }, [heatMapChangeTimeout, isPlayMode, status, windParamsSet.length]);

  useEffect(() => {
    setWindParams(windParamsSet[activeParamsIndex]);
  }, [activeParamsIndex, setWindParams, windParamsSet]);

  // transform + device-pixel snapping (replaces style.left)
  useEffect(() => {
    if (!listRef.current) {
      return;
    }
    const parent = listRef.current.parentElement as HTMLDivElement;
    const target = parent.offsetWidth / 2 - 12 - 24 * activeParamsIndex;
    listRef.current.style.transform = `translate3d(${pxSnap(target)}px,0,0)`;
  }, [activeParamsIndex]);

  const playPause = useMemo(
    () =>
      isPlayMode ? (
        cacheStats.progress < 100 ? (
          <CircularProgress
            size={24}
            className={windTimeBarClasses.playControlButton}
            variant="determinate"
            value={cacheStats.progress}
            onClick={() => handlePlayMode(false)}
          />
        ) : (
          <Box
            className={classNames(
              windTimeBarClasses.playControlPause,
              windTimeBarClasses.playControlButton,
            )}
            onClick={() => handlePlayMode(false)}
          >
            <StopIcon />
          </Box>
        )
      ) : (
        <Box
          className={classNames(
            windTimeBarClasses.playControlPlay,
            windTimeBarClasses.playControlButton,
          )}
          onClick={() => handlePlayMode(true)}
        >
          <PlayArrowIcon />
        </Box>
      ),
    [cacheStats.progress, handlePlayMode, isPlayMode],
  );

  return (
    <Root
      heatMapChangeTimeout={heatMapChangeTimeout}
      zeroTimeIndex={zeroTimeIndex}
    >
      <Box className={windTimeBarClasses.playControl}>{playPause}</Box>
      <Box className={windTimeBarClasses.timelineContainer}>
        <Box className={windTimeBarClasses.timelineListContainer}>
          <Box
            className={windTimeBarClasses.timelineMarkerCenter}
            aria-hidden
          />
          <List ref={listRef} className={windTimeBarClasses.timelineList}>
            {windParamsSet.map((params, index) => {
              const dist = Math.abs(activeParamsIndex - index);
              const scale = Math.max(0.7, 1 - dist * 0.1);
              const opacity = Math.max(0, 1 - dist / 3);

              return (
                <ListItem
                  key={[params.dref, params.tref, params.fhr].join('.')}
                  className={windTimeBarClasses.timelineListItem}
                  onClick={() => setActiveParamsIndex(index)}
                  sx={{
                    opacity,
                    transform: `scale(${scale})`,
                  }}
                />
              );
            })}
          </List>
        </Box>
        <Box className={windTimeBarClasses.timelineLabel}>
          {windParams.dateTime?.tz().format('HH:mm z') || ''}
        </Box>
      </Box>
    </Root>
  );
};
